import type { Dictionary, RoleAssignmentMessages, UserRoleManagementMessages } from '../models'
import { LocaleEnum } from '../enums'

const ROLE_ASSIGNMENT_MESSAGES_EN: RoleAssignmentMessages = {
  actions: {
    add: 'Assign role',
  },
  select: {
    title: 'Select roles',
    actions: {
      accept: 'Assign',
    },
  },
  conditions: {
    instance: {
      label: 'instance',
    },
    'role-id': {
      label: 'role',
    },
    'access-level': {
      label: 'access level',
    },
    add: {
      label: 'Add a new value for this condition',
    },
    select: {
      label: 'Select the value for this condition',
      selectAll: 'Select all',
    },
    invalid: 'Some conditions have not been configured ({value})',
  },
}

const ROLE_ASSIGNMENT_MESSAGES_ES: RoleAssignmentMessages = {
  actions: {
    add: '',
  },
  select: {
    title: '',
    actions: {
      accept: '',
    },
  },
  conditions: {
    instance: {
      label: '',
    },
    'role-id': {
      label: '',
    },
    'access-level': {
      label: '',
    },
    add: {
      label: '',
    },
    select: {
      label: '',
      selectAll: '',
    },
    invalid: '',
  },
}

export const USER_ROLE_MANAGEMENT_MESSAGES_EN: UserRoleManagementMessages = {
  label: 'Users Management',
  search: {
    placeholder: 'Search',
  },
  filters: {
    title: 'Select filters',
    role: {
      label: 'Role',
    },
    accept: 'Apply filters',
  },
  instances: {
    tooltip: '<b>Instances</b><br />{instancesIds}',
  },
  fields: {
    user: {
      label: 'User',
      you: '{user} (you)',
    },
    instances: {
      label: 'Instances',
    },
    roles: {
      label: 'Roles',
    },
  },
  options: {
    edit: 'Edit',
    delete: 'Delete',
  },
  details: {
    title: '{user} - instances ({instancesCount})',
    actions: {
      edit: 'Edit user',
      details: 'See details',
      close: 'Close',
    },
  },
  invite: {
    title: 'Invite user',
    actions: {
      accept: 'Invite',
      cancel: 'Cancel',
    },
    success: 'User invite(s) sent successfully',
    error: 'Error sending user invite(s)',
    sections: {
      details: {
        title: 'Details',
        email: {
          label: 'Email',
          error: 'One of the emails entered is not valid',
        },
      },
      roles: {
        ...ROLE_ASSIGNMENT_MESSAGES_EN,
        title: 'Roles',
      },
    },
  },
  edit: {
    title: 'Edit user',
    details: 'User details',
    sections: {
      details: {
        title: 'Details',
        fields: {
          email: {
            label: 'Email',
          },
          firstName: {
            label: 'First name',
          },
          lastName: {
            label: 'Last name',
          },
        },
      },
      roles: {
        ...ROLE_ASSIGNMENT_MESSAGES_EN,
        title: 'Roles',
      },
    },
    actions: {
      accept: 'Save',
      cancel: 'Cancel',
    },
    success: 'User edited successfully',
    error: 'Error editing user',
  },
  delete: {
    title: 'Delete user',
    message: 'Are you sure you want to delete the selected user ({user})?',
    actions: {
      accept: 'Delete',
      cancel: 'Cancel',
    },
    success: 'User deleted successfully',
  },
  account: {
    fields: {
      firstName: {
        label: 'First name',
      },
      lastName: {
        label: 'Last name',
      },
    },
    changePassword: {
      title: 'Change password',
      fields: {
        currentPassword: {
          label: 'Current password',
        },
        newPassword: {
          label: 'New password',
          error:
            'Password must include at least one uppercase and lowercase letter, one number, one special character and be between 8 and 40 characters long',
        },
        repeatNewPassword: {
          label: 'Repeat new password',
          error: 'Passwords do not match',
        },
      },
      actions: {
        accept: 'Confirm',
        cancel: 'Cancel',
      },
      success: 'Password changed successfuly',
    },
    actions: {
      accept: 'Save',
    },
    update: {
      success: 'User edited successfully',
    },
  },
}

export const USER_ROLE_MANAGEMENT_MESSAGES_ES: UserRoleManagementMessages = {
  label: '',
  search: {
    placeholder: '',
  },
  filters: {
    title: '',
    role: {
      label: '',
    },
    accept: '',
  },
  instances: {
    tooltip: '',
  },
  fields: {
    user: {
      label: '',
      you: '',
    },
    instances: {
      label: '',
    },
    roles: {
      label: '',
    },
  },
  options: {
    edit: '',
    delete: '',
  },
  details: {
    title: '',
    actions: {
      edit: '',
      details: '',
      close: '',
    },
  },
  invite: {
    title: '',
    actions: {
      accept: '',
      cancel: '',
    },
    success: '',
    error: '',
    sections: {
      details: {
        title: '',
        email: {
          label: '',
          error: '',
        },
      },
      roles: {
        ...ROLE_ASSIGNMENT_MESSAGES_ES,
        title: '',
      },
    },
  },
  edit: {
    title: '',
    details: '',
    sections: {
      details: {
        title: '',
        fields: {
          email: {
            label: '',
          },
          firstName: {
            label: '',
          },
          lastName: {
            label: '',
          },
        },
      },
      roles: {
        ...ROLE_ASSIGNMENT_MESSAGES_ES,
        title: '',
      },
    },
    actions: {
      accept: '',
      cancel: '',
    },
    success: '',
    error: '',
  },
  delete: {
    title: '',
    message: '',
    actions: {
      accept: '',
      cancel: '',
    },
    success: '',
  },
  account: {
    fields: {
      firstName: {
        label: '',
      },
      lastName: {
        label: '',
      },
    },
    changePassword: {
      title: '',
      fields: {
        currentPassword: {
          label: '',
        },
        newPassword: {
          label: '',
          error: '',
        },
        repeatNewPassword: {
          label: '',
          error: '',
        },
      },
      actions: {
        accept: '',
        cancel: '',
      },
      success: '',
    },
    actions: {
      accept: '',
    },
    update: {
      success: '',
    },
  },
}

export const USER_ROLE_MANAGEMENT_MESSAGES: Dictionary<{
  userRoleManagement: UserRoleManagementMessages
}> = {
  [LocaleEnum.ENGLISH]: {
    userRoleManagement: USER_ROLE_MANAGEMENT_MESSAGES_EN,
  },
  [LocaleEnum.SPANISH]: {
    userRoleManagement: USER_ROLE_MANAGEMENT_MESSAGES_ES,
  },
}
